/**
 * 💬 SERVICE DE CHAT
 * 
 * Gestion des messages de chat entre conducteurs et passagers
 */

import { database } from '../firebase';
import { ref, push, onValue, off } from 'firebase/database';

/**
 * Génère un ID unique pour une conversation
 * @param {string} requestId - ID de la demande
 * @param {string} driverName - Nom du conducteur
 * @param {string} passengerName - Nom du passager
 * @returns {string} ID de la conversation
 */
export function getChatId(requestId, driverName, passengerName) {
  // Créer un ID unique et stable pour la conversation
  const participants = [driverName, passengerName].sort().join('_');
  return `chat_${requestId}_${participants}`;
}

/**
 * Envoyer un message dans une conversation
 * @param {string} chatId - ID de la conversation
 * @param {string} senderName - Nom de l'expéditeur
 * @param {string} message - Contenu du message
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export async function sendMessage(chatId, senderName, message) {
  if (!message || !message.trim()) {
    return { success: false, error: 'Le message ne peut pas être vide' };
  }

  try {
    const messagesRef = ref(database, `chats/${chatId}/messages`);
    
    await push(messagesRef, {
      senderName,
      message: message.trim(),
      timestamp: Date.now()
    });

    return { success: true };
  } catch (error) {
    console.error('❌ [chatService] Erreur envoi message:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Écouter les messages d'une conversation
 * @param {string} chatId - ID de la conversation
 * @param {Function} callback - Fonction appelée avec les messages
 * @returns {Function} Fonction pour se désabonner
 */
export function listenToMessages(chatId, callback) {
  if (typeof callback !== 'function') {
    console.error('❌ [chatService] Callback invalide !');
    return () => {};
  }

  try {
    const messagesRef = ref(database, `chats/${chatId}/messages`);
    
    const unsubscribe = onValue(messagesRef, (snapshot) => {
      try {
        if (!snapshot.exists()) {
          callback([]);
          return;
        }

        const rawData = snapshot.val();
        const messages = Object.entries(rawData)
          .map(([id, data]) => ({
            id,
            ...data
          }))
          .sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0));

        callback(messages);
      } catch (error) {
        console.error('❌ [chatService] Erreur traitement messages:', error);
        callback([]);
      }
    }, (error) => {
      console.error('❌ [chatService] Erreur listener messages:', error);
      callback([]);
    });

    return unsubscribe;
  } catch (error) {
    console.error('❌ [chatService] Erreur setup listener:', error);
    return () => {};
  }
}

/**
 * Marquer les messages comme lus (optionnel, pour notifications futures)
 * @param {string} chatId - ID de la conversation
 * @param {string} userName - Nom de l'utilisateur qui a lu
 */
export async function markAsRead(chatId, userName) {
  try {
    const readRef = ref(database, `chats/${chatId}/read/${userName}`);
    await push(readRef, {
      timestamp: Date.now()
    });
  } catch (error) {
    console.error('❌ [chatService] Erreur marquage lu:', error);
  }
}

/**
 * Marquer un chat comme lu dans localStorage
 * @param {string} chatId - ID de la conversation
 */
export function markChatAsReadLocal(chatId) {
  try {
    const key = `chat_read_${chatId}`;
    localStorage.setItem(key, Date.now().toString());
  } catch (error) {
    console.error('❌ [chatService] Erreur marquage lu local:', error);
  }
}

/**
 * Obtenir le timestamp du dernier message lu pour un chat
 * @param {string} chatId - ID de la conversation
 * @returns {number} Timestamp du dernier message lu, ou 0 si jamais lu
 */
export function getLastReadTimestamp(chatId) {
  try {
    const key = `chat_read_${chatId}`;
    const timestamp = localStorage.getItem(key);
    return timestamp ? Number(timestamp) : 0;
  } catch (error) {
    return 0;
  }
}

/**
 * Vérifier s'il y a des messages dans un chat
 * @param {string} chatId - ID de la conversation
 * @param {Function} callback - Fonction appelée avec true/false selon s'il y a des messages
 * @returns {Function} Fonction pour se désabonner
 */
export function hasMessages(chatId, callback) {
  if (typeof callback !== 'function') {
    console.error('❌ [chatService] Callback invalide !');
    return () => {};
  }

  try {
    const messagesRef = ref(database, `chats/${chatId}/messages`);
    
    const unsubscribe = onValue(messagesRef, (snapshot) => {
      try {
        const hasMessages = snapshot.exists() && snapshot.val() !== null;
        callback(hasMessages);
      } catch (error) {
        console.error('❌ [chatService] Erreur vérification messages:', error);
        callback(false);
      }
    }, (error) => {
      console.error('❌ [chatService] Erreur listener messages:', error);
      callback(false);
    });

    return unsubscribe;
  } catch (error) {
    console.error('❌ [chatService] Erreur setup listener messages:', error);
    return () => {};
  }
}
